import { CONFIG } from './config.js';
import { sendToolCallToApp, isAppOpen, getCurrentApp } from './modules/apps.js';

// ========== AI Functions ==========

// AI Function: Change Language
function changeLanguageByAI(languageCode) {
    if (typeof i18n !== 'undefined' && i18n.setLanguage) {
        const success = i18n.setLanguage(languageCode);
        if (success) {
            // showToast(`Sprache zu ${languageCode.toUpperCase()} gewechselt`, 'success', 3000);
            console.log(`AI changed language to: ${languageCode}`);
        }
        return success;
    }
    return false;
}

// AI Function: Trigger Confetti Effect
function triggerConfetti() {
    const container = document.getElementById('confettiContainer');
    // Get current theme color from CSS variable
    const currentThemeColor = getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim();
    const colors = [currentThemeColor, currentThemeColor, '#FF4444', '#00FF66', '#0055AA', '#FFD700', '#FF69B4', '#9370DB'];
    const particleCount = 150;

    for (let i = 0; i < particleCount; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + '%';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.width = (Math.random() * 10 + 5) + 'px';
        confetti.style.height = (Math.random() * 10 + 5) + 'px';
        confetti.style.animationDuration = (Math.random() * 3 + 2) + 's';
        confetti.style.animationDelay = (Math.random() * 0.5) + 's';

        container.appendChild(confetti);

        // Remove after animation
        setTimeout(() => {
            confetti.remove();
        }, 5500);
    }

    console.log('AI triggered confetti effect');
    return true;
}

// AI Function: Flash Screen
function flashScreen(color, duration = 500) {
    // If no color provided, use current theme color
    if (!color) {
        color = getComputedStyle(document.documentElement).getPropertyValue('--accent-color').trim();
    }

    const flash = document.createElement('div');
    flash.className = 'screen-flash';
    flash.style.backgroundColor = color;
    flash.style.animationDuration = (duration / 1000) + 's';
    document.body.appendChild(flash);

    setTimeout(() => {
        flash.remove();
    }, duration);

    console.log(`AI triggered screen flash: ${color}`);
    return true;
}

// AI Function: Shake Screen
function shakeScreen() {
    document.body.classList.add('shake');
    setTimeout(() => {
        document.body.classList.remove('shake');
    }, 500);

    console.log('AI triggered screen shake');
    return true;
}

// Helper function: Convert hex color to RGB
function hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    return { r, g, b };
}

// Helper function: Convert RGB string to hex
function rgbToHex(rgbString) {
    // Handle rgb(r, g, b) or rgba(r, g, b, a) format
    const match = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return null;

    const r = parseInt(match[1]);
    const g = parseInt(match[2]);
    const b = parseInt(match[3]);

    return '#' + [r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('');
}

// Helper function: Calculate relative luminance (WCAG formula)
function getLuminance(r, g, b) {
    // Convert RGB to 0-1 range
    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    // Apply gamma correction
    const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    // Calculate luminance
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

// Helper function: Determine if color is light or dark
function isLightColor(color) {
    // Convert rgb() to hex if needed
    if (color.startsWith('rgb')) {
        color = rgbToHex(color);
        if (!color) return false; // Fallback if conversion fails
    }

    const rgb = hexToRgb(color);
    const luminance = getLuminance(rgb.r, rgb.g, rgb.b);

    // Threshold: 0.5 means 50% luminance
    // Light colors have higher luminance
    return luminance > 0.5;
}

// Helper function: Get contrasting text color
function getContrastingTextColor(backgroundColor) {
    return isLightColor(backgroundColor) ? '#121212' : '#ffffff';
}

// AI Function: Change Theme Color
function changeThemeColor(color) {
    // Change CSS custom properties or specific colors
    document.documentElement.style.setProperty('--accent-color', color);

    // Automatically adjust text color for user message bubbles based on theme color brightness
    const textColor = getContrastingTextColor(color);
    document.documentElement.style.setProperty('--user-message-text-color', textColor);

    // Also adjust the "Du" label color for better contrast
    const labelColor = isLightColor(color) ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 245, 230, 0.9)';
    document.documentElement.style.setProperty('--user-message-label-color', labelColor);

    // Update button borders and other orange elements
    const style = document.createElement('style');
    style.innerHTML = `
                #btn { border-color: ${color} !important; color: ${color} !important; box-shadow: 0 0 20px ${color}66 !important; }
                #settingsIcon { color: ${color} !important; }
                .modal-content { border-color: ${color} !important; }
                .modal-header h2 { color: ${color} !important; }
                .modal-header { border-bottom-color: ${color} !important; }
            `;
    style.id = 'ai-theme-override';

    // Remove old override if exists
    const oldStyle = document.getElementById('ai-theme-override');
    if (oldStyle) oldStyle.remove();

    document.head.appendChild(style);

    // showToast(`Farbe zu ${color} geändert`, 'info', 3000);
    console.log(`AI changed theme color to: ${color}`);
    return true;
}

// AI Function: Show Notification Banner
function showNotificationBanner(message, duration = 3000) {
    const banner = document.createElement('div');
    banner.className = 'notification-banner';
    banner.textContent = message;
    document.body.appendChild(banner);

    setTimeout(() => {
        banner.style.animation = 'slideDownBanner 0.5s ease-out reverse';
        setTimeout(() => {
            banner.remove();
        }, 500);
    }, duration);

    console.log(`Notification banner displayed for ${duration}ms: ${message}`);
    return true;
}

// ========== Expose UI Functions Globally ==========
// Make all UI action functions available on the window object
// This allows dynamic calling via WebSocket ui_action messages

window.changeLanguageByAI = changeLanguageByAI;
window.triggerConfetti = triggerConfetti;
window.flashScreen = flashScreen;
window.shakeScreen = shakeScreen;
window.changeThemeColor = changeThemeColor;
window.showNotificationBanner = showNotificationBanner;
window.sendUIConfig = sendUIConfig;

// Helper for modules to check WebSocket state
window._isWebSocketOpen = function() {
    return socket && socket.readyState === WebSocket.OPEN;
};

// Helper for modules to send messages via the WebSocket
window._sendWebSocketMessage = function(msg) {
    if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(msg));
    } else {
        console.warn('Cannot send message: WebSocket not open', msg);
    }
};

// Main AI Function Call Handler
function handleAIFunctionCall(functionName, args = {}) {
    console.log(`AI Function Call: ${functionName}`, args);

    switch (functionName) {
        case 'change_language':
        case 'changeLanguage':
            return changeLanguageByAI(args.language || args.languageCode || args.lang);

        case 'trigger_confetti':
        case 'triggerConfetti':
        case 'confetti':
            return triggerConfetti();

        case 'flash_screen':
        case 'flashScreen':
            return flashScreen(args.color, args.duration);

        case 'shake_screen':
        case 'shakeScreen':
            return shakeScreen();

        case 'change_theme':
        case 'changeTheme':
            return changeThemeColor(args.color);

        case 'show_notification':
        case 'showNotification':
            return showNotificationBanner(args.message, args.duration);

        default:
            console.warn(`Unknown AI function: ${functionName}`);
            return false;
    }
}

// ========== Toast Notification System ==========

// Parse error messages to extract human-readable content
function parseErrorMessage(rawMessage) {
    if (!rawMessage) return window.i18n?.t('notifications.unknownError') || 'An unknown error occurred';

    // Check if message contains JSON error structure
    if (rawMessage.includes('{') && rawMessage.includes('"detail"')) {
        try {
            // Extract service name from prefix (e.g., "ElevenLabs Error 401:")
            const serviceMatch = rawMessage.match(/^([\w\s]+)(?:Error|error)\s+(\d+)?:?/i);
            const serviceName = serviceMatch ? serviceMatch[1].trim() : '';
            const errorCode = serviceMatch && serviceMatch[2] ? serviceMatch[2] : '';

            // Extract JSON part
            const jsonMatch = rawMessage.match(/{.*}/);
            if (jsonMatch) {
                const errorData = JSON.parse(jsonMatch[0]);

                // Build human-readable message
                let message = '';

                if (serviceName) {
                    message += serviceName;
                    if (errorCode) message += ` (${errorCode})`;
                    message += ': ';
                }

                // Extract the actual error message from various JSON structures
                if (errorData.detail?.message) {
                    message += errorData.detail.message;
                } else if (errorData.detail) {
                    message += typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail);
                } else if (errorData.message) {
                    message += errorData.message;
                } else if (errorData.error) {
                    message += errorData.error;
                } else {
                    // Fallback to original message if we can't parse it
                    return rawMessage;
                }

                return message;
            }
        } catch (e) {
            console.warn('Failed to parse error message:', e);
            // Return original message if parsing fails
        }
    }

    // Return as-is if no JSON detected
    return rawMessage;
}

function showToast(message, type = 'error', duration = 5000) {
    const toastContainer = document.getElementById('toastContainer');

    // Create toast element
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;

    // Set icon based on type
    const icons = {
        error: '❌',
        success: '✅',
        info: 'ℹ️'
    };

    // Set title based on type
    const titles = {
        error: i18n.t('notifications.error'),
        success: i18n.t('notifications.success'),
        info: i18n.t('notifications.info')
    };


    toast.innerHTML = `
                <div class="toast-icon">${icons[type] || icons.error}</div>
                <div class="toast-content">
                    <div class="toast-title">${titles[type] || titles.error}</div>
                    <div class="toast-message">${message}</div>
                </div>
                <button class="toast-close" onclick="this.parentElement.remove()">×</button>
            `;

    toastContainer.appendChild(toast);

    // Auto-remove after duration
    if (duration > 0) {
        setTimeout(() => {
            toast.style.animation = 'slideInRight 0.3s ease-out reverse';
            setTimeout(() => toast.remove(), 300);
        }, duration);
    }
}

// ========== Settings Management ==========
const API_BASE = CONFIG.WS_API_BASE;
const settingsModal = document.getElementById('settingsModal');
const settingsIcon = document.getElementById('settingsIcon');
const closeModalBtn = document.getElementById('closeModal');
const cancelBtn = document.getElementById('cancelBtn');
const saveBtn = document.getElementById('saveBtn');
const systemPromptTextarea = document.getElementById('systemPrompt');
const statusMessage = document.getElementById('statusMessage');

// Fetch current system prompt from server
async function fetchSystemPrompt() {
    if (!systemPromptTextarea) return;
    try {
        const response = await fetch(`${API_BASE}/system-prompt`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        systemPromptTextarea.value = data.system_prompt || '';
    } catch (error) {
        console.error('Error loading system prompt:', error);
        showStatus((window.i18n?.t('settings.systemPrompt.loadError') || 'Error loading system prompt') + ': ' + error.message, 'error');
        systemPromptTextarea.value = '';
    }
}

// Save system prompt to server
async function saveSystemPrompt() {
    if (!systemPromptTextarea) return;
    const newPrompt = systemPromptTextarea.value.trim();

    if (!newPrompt) {
        showStatus(window.i18n?.t('settings.systemPrompt.emptyError') || 'System prompt cannot be empty!', 'error');
        return;
    }

    if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = window.i18n?.t('common.saving') || 'Saving...';
    }

    try {
        const response = await fetch(`${API_BASE}/system-prompt`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ system_prompt: newPrompt })
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        showStatus(window.i18n?.t('settings.systemPrompt.saveSuccess') || '✓ System prompt saved successfully!', 'success');

        // Auto-close modal after 1.5 seconds
        setTimeout(() => {
            closeSettings();
        }, 1500);

    } catch (error) {
        console.error('Error saving:', error);
        showStatus((window.i18n?.t('settings.systemPrompt.saveError') || 'Error saving') + ': ' + error.message, 'error');
    } finally {
        if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = window.i18n?.t('common.save') || 'Save';
        }
    }
}

// Show status message
function showStatus(message, type) {
    if (!statusMessage) return;
    statusMessage.textContent = message;
    statusMessage.className = `status-message ${type}`;

    if (type === 'success') {
        setTimeout(() => {
            if (statusMessage) statusMessage.className = 'status-message';
        }, 3000);
    }
}

// Open settings modal
function openSettings() {
    if (!settingsModal) return;
    settingsModal.classList.add('show');
    fetchSystemPrompt();
    if (statusMessage) statusMessage.className = 'status-message'; // Clear any previous status
    initializeUISettings(); // Initialize UI settings (theme color picker, etc.)
}

// Close settings modal
function closeSettings() {
    if (settingsModal) settingsModal.classList.remove('show');
    if (statusMessage) statusMessage.className = 'status-message'; // Clear status
}

// Event listeners
if (settingsIcon) settingsIcon.addEventListener('click', openSettings);
if (closeModalBtn) closeModalBtn.addEventListener('click', closeSettings);
if (cancelBtn) cancelBtn.addEventListener('click', closeSettings);
if (saveBtn) saveBtn.addEventListener('click', saveSystemPrompt);

// Close modal when clicking outside
if (settingsModal) {
    settingsModal.addEventListener('click', (e) => {
        if (e.target === settingsModal) {
            closeSettings();
        }
    });
}

// Close modal with ESC key
document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && settingsModal.classList.contains('show')) {
        closeSettings();
    }
});

// Load system prompt on page load
window.addEventListener('load', () => {
    fetchSystemPrompt();

    // Initialize theme on page load
    const { theme, color } = ThemeManager.loadTheme();
    ThemeManager.applyTheme(theme);
    ThemeManager.setAccentColor(color);
});

// ========== Tab Switching ==========
const tabs = document.querySelectorAll('.settings-tab');
const tabContents = document.querySelectorAll('.tab-content');

tabs.forEach(tab => {
    tab.addEventListener('click', () => {
        const tabId = tab.dataset.tab;

        // Update tab buttons
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');

        // Update tab contents
        tabContents.forEach(content => content.classList.remove('active'));
        document.getElementById('tab-' + tabId).classList.add('active');

        // Load content when switching tabs
        if (tabId === 'ui-settings') {
            initializeUISettings();
        } else if (tabId === 'llm-profiles') {
            fetchProfiles();
        } else if (tabId === 'tts-profiles') {
            fetchTTSProfiles();
        } else if (tabId === 'language') {
            renderLanguageOptions();
        }
    });
});

// ========== Theme Management ==========
const ThemeManager = {
    // Load theme from localStorage
    loadTheme() {
        const savedTheme = localStorage.getItem('appTheme') || 'dark';
        const savedColor = localStorage.getItem('appAccentColor') || '#00FF66';
        return { theme: savedTheme, color: savedColor };
    },

    // Save theme to localStorage
    saveTheme(theme, color) {
        localStorage.setItem('appTheme', theme);
        localStorage.setItem('appAccentColor', color);
    },

    // Apply theme to document
    applyTheme(theme) {
        document.documentElement.setAttribute('data-theme', theme);

        // Update theme toggle buttons
        const darkBtn = document.getElementById('darkModeBtn');
        const lightBtn = document.getElementById('lightModeBtn');

        if (darkBtn && lightBtn) {
            if (theme === 'light') {
                darkBtn.classList.remove('active');
                lightBtn.classList.add('active');
            } else {
                darkBtn.classList.add('active');
                lightBtn.classList.remove('active');
            }
        }

        // Reapply current accent color to adjust for new theme brightness
        const currentColor = localStorage.getItem('appAccentColor') || '#00FF66';
        this.setAccentColor(currentColor);

        // Update images based on theme
        const images = document.querySelectorAll('img[data-light-src][data-dark-src]');
        images.forEach(img => {
            if (theme === 'light') {
                img.src = img.getAttribute('data-light-src');
            } else {
                img.src = img.getAttribute('data-dark-src');
            }
        });
    },

    // Set accent color
    setAccentColor(color) {
        // Check current theme
        const currentTheme = document.documentElement.getAttribute('data-theme') || 'dark';

        // In light mode, darken the accent color for better contrast
        let finalColor = color;
        if (currentTheme === 'light') {
            finalColor = this.adjustBrightness(color, -30); // 30% darker in light mode
        }

        document.documentElement.style.setProperty('--accent-color', finalColor);

        // Also update related colors
        const rgb = this.hexToRgb(finalColor);
        const lighterColor = this.adjustBrightness(finalColor, 10);
        const darkerColor = this.adjustBrightness(finalColor, -10);

        document.documentElement.style.setProperty('--accent-light', lighterColor);
        document.documentElement.style.setProperty('--accent-dark', darkerColor);

        // Update text contrast for user messages
        const textColor = this.getContrastingTextColor(finalColor);
        document.documentElement.style.setProperty('--user-message-text-color', textColor);

        const labelColor = this.isLightColor(finalColor) ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 245, 230, 0.9)';
        document.documentElement.style.setProperty('--user-message-label-color', labelColor);
    },

    // Helper: Convert hex to RGB
    hexToRgb(hex) {
        hex = hex.replace(/^#/, '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return { r, g, b };
    },

    // Helper: Adjust brightness
    adjustBrightness(hex, percent) {
        const rgb = this.hexToRgb(hex);
        const adjust = (val) => Math.max(0, Math.min(255, val + (val * percent / 100)));
        const r = Math.round(adjust(rgb.r)).toString(16).padStart(2, '0');
        const g = Math.round(adjust(rgb.g)).toString(16).padStart(2, '0');
        const b = Math.round(adjust(rgb.b)).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    },

    // Helper: Check if color is light
    isLightColor(color) {
        const rgb = this.hexToRgb(color);
        const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
        return luminance > 0.5;
    },

    // Helper: Get contrasting text color
    getContrastingTextColor(backgroundColor) {
        return this.isLightColor(backgroundColor) ? '#121212' : '#ffffff';
    }
};

// Available theme colors
const THEME_COLORS = [
    { hex: '#00FF66', name: 'Neon Green' },
    { hex: '#FF8C00', name: 'Dark Orange' },
    { hex: '#00D4FF', name: 'Cyan' },
    { hex: '#FF1493', name: 'Deep Pink' },
    { hex: '#9370DB', name: 'Medium Purple' },
    { hex: '#FFD700', name: 'Gold' },
    { hex: '#FF4444', name: 'Red' },
    { hex: '#00CED1', name: 'Turquoise' },
    { hex: '#FF69B4', name: 'Hot Pink' },
    { hex: '#32CD32', name: 'Lime Green' },
    { hex: '#FF6347', name: 'Tomato' },
    { hex: '#4169E1', name: 'Royal Blue' }
];

// Initialize UI Settings
function initializeUISettings() {
    const { theme, color } = ThemeManager.loadTheme();

    // Render color picker
    renderThemeColorPicker(color);

    // Initialize background picker
    if (typeof window.initBackgroundPicker === 'function') {
        window.initBackgroundPicker();
    }

    // Setup theme toggle buttons
    const darkBtn = document.getElementById('darkModeBtn');
    const lightBtn = document.getElementById('lightModeBtn');

    if (darkBtn && lightBtn) {
        darkBtn.onclick = () => {
            ThemeManager.applyTheme('dark');
            ThemeManager.saveTheme('dark', ThemeManager.loadTheme().color);
        };

        lightBtn.onclick = () => {
            ThemeManager.applyTheme('light');
            ThemeManager.saveTheme('light', ThemeManager.loadTheme().color);
        };
    }
}

// Render theme color picker
function renderThemeColorPicker(currentColor) {
    const grid = document.getElementById('themeColorPickerGrid');
    if (!grid) return;

    grid.innerHTML = THEME_COLORS.map(color => `
        <div class="theme-color-option ${color.hex === currentColor ? 'active' : ''}" 
             style="background-color: ${color.hex};"
             data-color="${color.hex}"
             onclick="selectThemeColor('${color.hex}')">
        </div>
    `).join('');
}

// Select theme color
function selectThemeColor(color) {
    const { theme } = ThemeManager.loadTheme();

    // Apply color
    ThemeManager.setAccentColor(color);
    ThemeManager.saveTheme(theme, color);

    // Update UI
    renderThemeColorPicker(color);
}

// Make functions globally available
window.selectThemeColor = selectThemeColor;

// ========== Language Switching ==========
function renderLanguageOptions() {
    const languageGrid = document.getElementById('languageGrid');
    const languages = i18n.getAvailableLanguages();
    const currentLang = i18n.getCurrentLanguage();

    languageGrid.innerHTML = languages.map(lang => `
                <div class="language-option ${lang.code === currentLang ? 'active' : ''}" 
                     data-lang="${lang.code}" 
                     onclick="switchLanguage('${lang.code}')">
                    <div class="language-flag">${lang.flag}</div>
                    <div class="language-name">${lang.name}</div>
                </div>
            `).join('');
}

function switchLanguage(langCode) {
    if (i18n.setLanguage(langCode)) {
        // Re-render language options to update active state
        renderLanguageOptions();

        // Update all UI text
        updateUITranslations();

        // Show success notification
        // showToast(i18n.t('settings.language.description'), 'success', 2000);
    }
}

function updateUITranslations() {
    // Helper function for safe element updates
    const safeUpdate = (selector, property, value, isId = false) => {
        const el = isId ? document.getElementById(selector) : document.querySelector(selector);
        if (el) el[property] = value;
    };

    // Update settings modal title
    safeUpdate('.modal-header h2', 'textContent', '⚙️ ' + i18n.t('settings.title'));

    // Update tab labels (only existing tabs)
    safeUpdate('[data-tab="language"]', 'textContent', i18n.t('settings.tabs.language'));
    safeUpdate('[data-tab="ui-settings"]', 'textContent', i18n.t('settings.tabs.uiSettings'));

    // Update language tab
    safeUpdate('.language-description', 'textContent', i18n.t('settings.language.description'));

    // Update main UI
    safeUpdate('label', 'textContent', i18n.t('chat.ready'), true);
    safeUpdate('btn', 'textContent', i18n.t('chat.start'), true);

    // Update chat input
    safeUpdate('chatInput', 'placeholder', i18n.t('chat.inputPlaceholder'), true);
    safeUpdate('sendMessageBtn', 'title', i18n.t('chat.send'), true);

    // Note: We do NOT reset the output/chat history when changing language
    // Only the interface language is changed, chat content is preserved
}

// Make switchLanguage globally available
window.switchLanguage = switchLanguage;


// ========== LLM Profile Management ==========
const profileList = document.getElementById('profileList');
const profileForm = document.getElementById('profileForm');
const profileFormTitle = document.getElementById('profileFormTitle');
const addProfileBtn = document.getElementById('addProfileBtn');
const cancelProfileBtn = document.getElementById('cancelProfileBtn');
const saveProfileBtn = document.getElementById('saveProfileBtn');
const toggleApiKeyBtn = document.getElementById('toggleApiKey');
const profileStatusMessage = document.getElementById('profileStatusMessage');

// Profile form fields
const profileEditId = document.getElementById('profileEditId');
const profileName = document.getElementById('profileName');
const profileApiUrl = document.getElementById('profileApiUrl');
const profileApiKey = document.getElementById('profileApiKey');
const profileModelName = document.getElementById('profileModelName');

// Toggle API Key visibility
if (toggleApiKeyBtn) {
    toggleApiKeyBtn.addEventListener('click', () => {
        if (profileApiKey.type === 'password') {
            profileApiKey.type = 'text';
            toggleApiKeyBtn.textContent = '🙈';
        } else {
            profileApiKey.type = 'password';
            toggleApiKeyBtn.textContent = '👁️';
        }
    });
}

// Fetch all profiles
async function fetchProfiles() {
    try {
        // Fetch all profiles and active profile in parallel
        const [profilesResponse, activeResponse] = await Promise.all([
            fetch(`${API_BASE}/profiles`),
            fetch(`${API_BASE}/profiles/active`)
        ]);

        if (!profilesResponse.ok) throw new Error(`HTTP ${profilesResponse.status}`);
        const profiles = await profilesResponse.json();

        // Get active profile ID
        let activeProfileId = null;
        if (activeResponse.ok) {
            const activeProfile = await activeResponse.json();
            activeProfileId = activeProfile.id;
        }

        // Mark the active profile
        profiles.forEach(p => {
            p.is_active = (p.id === activeProfileId);
        });

        renderProfiles(profiles);
    } catch (error) {
        console.error('Error loading profiles:', error);
        if (profileList) profileList.innerHTML = `<div class="empty-state">${i18n.t('settings.llmProfiles.loadError')}: ${error.message}</div>`;
    }
}

// Render profiles list
function renderProfiles(profiles) {
    if (!profileList) return;
    if (!profiles || profiles.length === 0) {
        profileList.innerHTML = `<div class="empty-state">${i18n.t('settings.llmProfiles.emptyState')}</div>`;
        return;
    }

    // Ensure at least one profile is active (default to first if none)
    const hasActiveProfile = profiles.some(p => p.is_active);
    if (!hasActiveProfile && profiles.length > 0) {
        profiles[0].is_active = true;
    }

    profileList.innerHTML = profiles.map(profile => `
                <div class="profile-card ${profile.is_active ? 'active' : ''}" data-id="${profile.id}">
                    <div class="profile-card-header">
                        <span class="profile-name">
                            ${profile.is_active ? `<span class="active-label">✓ ${i18n.t('common.active')}</span>` : ''}
                            ${escapeHtml(profile.name)}
                        </span>
                        <div class="profile-actions">
                            ${!profile.is_active ? `<button class="profile-btn success" onclick="activateProfile(${profile.id})">${i18n.t('common.activate')}</button>` : `<span class="active-indicator">${i18n.t('common.inUse')}</span>`}
                            <button class="profile-btn" onclick="editProfile(${profile.id})">${i18n.t('common.edit')}</button>
                            <button class="profile-btn danger" onclick="deleteProfile(${profile.id}, '${escapeHtml(profile.name)}')">${i18n.t('common.delete')}</button>
                        </div>
                    </div>
                    <div class="profile-model">${i18n.t('settings.llmProfiles.modelInfo', { model: escapeHtml(profile.model_name), api: escapeHtml(profile.api_url) })}</div>
                </div>
            `).join('');
}

// Helper function to escape HTML
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
}

// Show profile form for adding
function showProfileForm() {
    if (!profileForm) return;
    if (profileFormTitle) profileFormTitle.textContent = window.i18n?.t('settings.llmProfiles.newConnection') || 'New Connection';
    if (profileEditId) profileEditId.value = '';
    if (profileName) profileName.value = '';
    if (profileApiUrl) profileApiUrl.value = '';
    if (profileApiKey) profileApiKey.value = '';
    if (profileModelName) profileModelName.value = '';
    if (profileStatusMessage) profileStatusMessage.className = 'status-message';
    profileForm.classList.add('show');
    if (addProfileBtn) addProfileBtn.style.display = 'none';
}

// Hide profile form
function hideProfileForm() {
    if (profileForm) profileForm.classList.remove('show');
    if (addProfileBtn) addProfileBtn.style.display = 'block';
}

// Edit an existing profile
async function editProfile(id) {
    if (!profileForm) return;
    try {
        const response = await fetch(`${API_BASE}/profiles`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const profiles = await response.json();
        const profile = profiles.find(p => p.id === id);

        if (!profile) {
            alert(i18n.t('settings.llmProfiles.profileNotFound') || 'Profile not found!');
            return;
        }

        if (profileFormTitle) profileFormTitle.textContent = i18n.t('settings.llmProfiles.editConnection') || 'Edit Connection';
        if (profileEditId) profileEditId.value = profile.id;
        if (profileName) profileName.value = profile.name;
        if (profileApiUrl) profileApiUrl.value = profile.api_url;
        if (profileApiKey) profileApiKey.value = profile.api_key;
        if (profileModelName) profileModelName.value = profile.model_name;
        if (profileStatusMessage) profileStatusMessage.className = 'status-message';
        profileForm.classList.add('show');
        if (addProfileBtn) addProfileBtn.style.display = 'none';
    } catch (error) {
        alert((i18n.t('settings.llmProfiles.loadError') || 'Error loading profile') + ': ' + error.message);
    }
}

// Save profile (create or update)
async function saveProfile() {
    if (!profileName || !profileApiUrl || !profileApiKey || !profileModelName || !profileEditId) return;
    const name = profileName.value.trim();
    const apiUrl = profileApiUrl.value.trim();
    const apiKey = profileApiKey.value.trim();
    const modelName = profileModelName.value.trim();
    const editId = profileEditId.value;

    // Validation
    if (!name || !apiUrl || !apiKey || !modelName) {
        showProfileStatus(i18n.t('settings.llmProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    if (saveProfileBtn) {
        saveProfileBtn.disabled = true;
        saveProfileBtn.textContent = i18n.t('common.saving') || 'Saving...';
    }

    try {
        let response;
        const profileData = {
            name: name,
            api_url: apiUrl,
            api_key: apiKey,
            model_name: modelName
        };

        if (editId) {
            // Update existing profile - delete and recreate since API doesn't have PUT
            await fetch(`${API_BASE}/profiles/${editId}`, { method: 'DELETE' });
            response = await fetch(`${API_BASE}/profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        } else {
            // Create new profile
            response = await fetch(`${API_BASE}/profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        }

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        showProfileStatus(i18n.t('settings.llmProfiles.saveSuccess') || '✓ Profile saved successfully!', 'success');
        setTimeout(() => {
            hideProfileForm();
            fetchProfiles();
        }, 800);

    } catch (error) {
        showProfileStatus((i18n.t('settings.llmProfiles.saveError') || 'Error saving') + ': ' + error.message, 'error');
    } finally {
        if (saveProfileBtn) {
            saveProfileBtn.disabled = false;
            saveProfileBtn.textContent = i18n.t('common.save') || 'Save';
        }
    }
}

// Activate a profile
async function activateProfile(id) {
    try {
        const response = await fetch(`${API_BASE}/profiles/${id}/activate`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchProfiles(); // Reload the list
    } catch (error) {
        alert((i18n.t('settings.llmProfiles.activateError') || 'Error activating') + ': ' + error.message);
    }
}

// Delete a profile
async function deleteProfile(id, name) {
    if (!confirm(i18n.t('settings.llmProfiles.deleteConfirm', { name: name }))) {
        return;
    }

    try {
        const response = await fetch(`${API_BASE}/profiles/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchProfiles(); // Reload the list
    } catch (error) {
        alert((i18n.t('settings.llmProfiles.deleteError') || 'Error deleting') + ': ' + error.message);
    }
}

// Show status message in profile form
function showProfileStatus(message, type) {
    if (!profileStatusMessage) return;
    profileStatusMessage.textContent = message;
    profileStatusMessage.className = `status-message ${type}`;
    if (type === 'success') {
        setTimeout(() => {
            profileStatusMessage.className = 'status-message';
        }, 3000);
    }
}

// Event listeners for profile management
if (addProfileBtn) addProfileBtn.addEventListener('click', showProfileForm);
if (cancelProfileBtn) cancelProfileBtn.addEventListener('click', hideProfileForm);
if (saveProfileBtn) saveProfileBtn.addEventListener('click', saveProfile);

// Make functions globally available for onclick handlers
window.editProfile = editProfile;
window.activateProfile = activateProfile;
window.deleteProfile = deleteProfile;
window.editTTSProfile = editTTSProfile;
window.activateTTSProfile = activateTTSProfile;
window.deleteTTSProfile = deleteTTSProfile;

// ========== TTS Profile Management ==========
const ttsProfileList = document.getElementById('ttsProfileList');
const ttsProfileForm = document.getElementById('ttsProfileForm');
const ttsProfileFormTitle = document.getElementById('ttsProfileFormTitle');
const addTtsProfileBtn = document.getElementById('addTtsProfileBtn');
const cancelTtsProfileBtn = document.getElementById('cancelTtsProfileBtn');
const saveTtsProfileBtn = document.getElementById('saveTtsProfileBtn');
const toggleTtsApiKeyBtn = document.getElementById('toggleTtsApiKey');
const ttsProfileStatusMessage = document.getElementById('ttsProfileStatusMessage');

// TTS Profile form fields
const ttsProfileEditId = document.getElementById('ttsProfileEditId');
const ttsProfileName = document.getElementById('ttsProfileName');
const ttsProviderType = document.getElementById('ttsProviderType');
const ttsApiUrl = document.getElementById('ttsApiUrl');
const ttsApiKey = document.getElementById('ttsApiKey');
const ttsModelId = document.getElementById('ttsModelId');
const ttsVoiceId = document.getElementById('ttsVoiceId');

// Toggle TTS API Key visibility
if (toggleTtsApiKeyBtn) {
    toggleTtsApiKeyBtn.addEventListener('click', () => {
        if (ttsApiKey.type === 'password') {
            ttsApiKey.type = 'text';
            toggleTtsApiKeyBtn.textContent = '🙈';
        } else {
            ttsApiKey.type = 'password';
            toggleTtsApiKeyBtn.textContent = '👁️';
        }
    });
}

// Store profiles globally for validation
let currentTTSProfiles = [];

// Fetch all TTS profiles
async function fetchTTSProfiles() {
    try {
        // Fetch all profiles and active profile in parallel
        const [profilesResponse, activeResponse] = await Promise.all([
            fetch(`${API_BASE}/tts-profiles`),
            fetch(`${API_BASE}/tts-profiles/active`)
        ]);

        if (!profilesResponse.ok) throw new Error(`HTTP ${profilesResponse.status}`);
        const profiles = await profilesResponse.json();

        // Get active profile ID
        let activeProfileId = null;
        if (activeResponse.ok) {
            const activeProfile = await activeResponse.json();
            activeProfileId = activeProfile.id;
        }

        // Mark the active profile
        profiles.forEach(p => {
            p.is_active = (p.id === activeProfileId);
        });

        currentTTSProfiles = profiles; // Store for client-side validation

        renderTTSProfiles(profiles);
    } catch (error) {
        if (ttsProfileList) ttsProfileList.innerHTML = `<div class="empty-state">${i18n.t('settings.ttsProfiles.loadError') || 'Error loading TTS profiles'}: ${error.message}</div>`;
    }
}

// Render TTS profiles list
function renderTTSProfiles(profiles) {
    if (!ttsProfileList) return;
    if (!profiles || profiles.length === 0) {
        ttsProfileList.innerHTML = `<div class="empty-state">${i18n.t('settings.ttsProfiles.emptyState')}</div>`;
        return;
    }

    // Ensure at least one profile is active (default to first if none)
    const hasActiveProfile = profiles.some(p => p.is_active);
    if (!hasActiveProfile && profiles.length > 0) {
        profiles[0].is_active = true;
    }

    ttsProfileList.innerHTML = profiles.map(profile => `
                <div class="profile-card ${profile.is_active ? 'active' : ''}" data-id="${profile.id}">
                    <div class="profile-card-header">
                        <span class="profile-name">
                            ${profile.is_active ? `<span class="active-label">✓ ${i18n.t('common.active')}</span>` : ''}
                            ${escapeHtml(profile.name)}
                        </span>
                        <div class="profile-actions">
                            ${!profile.is_active ? `<button class="profile-btn success" onclick="activateTTSProfile(${profile.id})">${i18n.t('common.activate')}</button>` : `<span class="active-indicator">${i18n.t('common.inUse')}</span>`}
                            <button class="profile-btn" onclick="editTTSProfile(${profile.id})">${i18n.t('common.edit')}</button>
                            <button class="profile-btn danger" onclick="deleteTTSProfile(${profile.id}, '${escapeHtml(profile.name)}')">${i18n.t('common.delete')}</button>
                        </div>
                    </div>
                    <div class="profile-model">
                        <span style="background: #333; padding: 2px 6px; border-radius: 4px; font-size: 0.8em; margin-right: 5px;">${escapeHtml(profile.provider_type)}</span>
                        ${profile.voice_id ? `Voice ID: ${escapeHtml(profile.voice_id)}` : ''}
                    </div>
                </div>
            `).join('');
}

// Show TTS profile form for adding
function showTTSProfileForm() {
    if (!ttsProfileForm) return;
    if (ttsProfileFormTitle) ttsProfileFormTitle.textContent = i18n.t('settings.ttsProfiles.newVoice') || 'New Voice';
    if (ttsProfileEditId) ttsProfileEditId.value = '';
    if (ttsProfileName) ttsProfileName.value = '';
    if (ttsProviderType) ttsProviderType.value = 'piper';
    if (ttsApiUrl) ttsApiUrl.value = '';
    if (ttsApiKey) ttsApiKey.value = '';
    if (ttsModelId) ttsModelId.value = '';
    if (ttsVoiceId) ttsVoiceId.value = '';
    if (ttsProfileStatusMessage) ttsProfileStatusMessage.className = 'status-message';
    ttsProfileForm.classList.add('show');
    if (addTtsProfileBtn) addTtsProfileBtn.style.display = 'none';
}

// Hide TTS profile form
function hideTTSProfileForm() {
    if (ttsProfileForm) ttsProfileForm.classList.remove('show');
    if (addTtsProfileBtn) addTtsProfileBtn.style.display = 'block';
}

// Edit an existing TTS profile
async function editTTSProfile(id) {
    if (!ttsProfileForm) return;
    try {
        const response = await fetch(`${API_BASE}/tts-profiles`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const profiles = await response.json();
        const profile = profiles.find(p => p.id === id);

        if (!profile) {
            alert(i18n.t('settings.llmProfiles.profileNotFound') || 'Profile not found!');
            return;
        }

        if (ttsProfileFormTitle) ttsProfileFormTitle.textContent = i18n.t('settings.ttsProfiles.editVoice') || 'Edit Voice';
        if (ttsProfileEditId) ttsProfileEditId.value = profile.id;
        if (ttsProfileName) ttsProfileName.value = profile.name;
        if (ttsProviderType) ttsProviderType.value = profile.provider_type;
        if (ttsApiUrl) ttsApiUrl.value = profile.api_url || '';
        if (ttsApiKey) ttsApiKey.value = profile.api_key || '';
        if (ttsModelId) ttsModelId.value = profile.model_id || '';
        if (ttsVoiceId) ttsVoiceId.value = profile.voice_id || '';
        if (ttsProfileStatusMessage) ttsProfileStatusMessage.className = 'status-message';
        ttsProfileForm.classList.add('show');
        if (addTtsProfileBtn) addTtsProfileBtn.style.display = 'none';
    } catch (error) {
        alert((i18n.t('settings.ttsProfiles.loadError') || 'Error loading profile') + ': ' + error.message);
    }
}

// Save TTS profile (create or update)
async function saveTTSProfile() {
    if (!ttsProfileName || !ttsProviderType || !ttsApiUrl || !ttsApiKey || !ttsModelId || !ttsVoiceId || !ttsProfileEditId) return;
    const name = ttsProfileName.value.trim();
    const providerType = ttsProviderType.value;
    const apiUrl = ttsApiUrl.value.trim();
    const apiKey = ttsApiKey.value.trim();
    const modelId = ttsModelId.value.trim();
    const voiceId = ttsVoiceId.value.trim();
    const editId = ttsProfileEditId.value;

    // Validation
    if (!name) {
        showTTSProfileStatus(i18n.t('settings.ttsProfiles.allFieldsRequired') || 'All fields must be filled!', 'error');
        return;
    }

    // Check for duplicate name
    if (currentTTSProfiles.some(p => p.name.toLowerCase() === name.toLowerCase() && p.id != editId)) {
        showTTSProfileStatus('A profile with this name already exists!', 'error');
        return;
    }

    if (saveTtsProfileBtn) {
        saveTtsProfileBtn.disabled = true;
        saveTtsProfileBtn.textContent = i18n.t('common.saving') || 'Saving...';
    }

    try {
        let response;
        const profileData = {
            name: name,
            provider_type: providerType,
            api_url: apiUrl,
            api_key: apiKey,
            model_id: modelId,
            voice_id: voiceId
        };

        if (editId) {
            // Update existing profile - delete and recreate
            await fetch(`${API_BASE}/tts-profiles/${editId}`, { method: 'DELETE' });
            response = await fetch(`${API_BASE}/tts-profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        } else {
            // Create new profile
            response = await fetch(`${API_BASE}/tts-profiles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(profileData)
            });
        }

        if (!response.ok) {
            let errorMessage = `HTTP ${response.status}`;
            try {
                const errorData = await response.json();
                if (errorData.detail) {
                    errorMessage = errorData.detail;
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            } catch (e) {
                // If not JSON, try text
                const textError = await response.text();
                if (textError) errorMessage = textError;
            }
            throw new Error(errorMessage);
        }

        showTTSProfileStatus(i18n.t('settings.ttsProfiles.saveSuccess') || '✓ TTS profile saved successfully!', 'success');
        setTimeout(() => {
            hideTTSProfileForm();
            fetchTTSProfiles();
        }, 800);

    } catch (error) {
        showTTSProfileStatus((i18n.t('settings.ttsProfiles.saveError') || 'Error saving') + ': ' + error.message, 'error');
    } finally {
        if (saveTtsProfileBtn) {
            saveTtsProfileBtn.disabled = false;
            saveTtsProfileBtn.textContent = i18n.t('common.save') || 'Save';
        }
    }
}

// Activate a TTS profile
async function activateTTSProfile(id) {
    try {
        const response = await fetch(`${API_BASE}/tts-profiles/${id}/activate`, {
            method: 'POST'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchTTSProfiles(); // Reload the list
    } catch (error) {
        alert((i18n.t('settings.ttsProfiles.activateError') || 'Error activating') + ': ' + error.message);
    }
}

// Delete a TTS profile
async function deleteTTSProfile(id, name) {
    if (!confirm(i18n.t('settings.ttsProfiles.deleteConfirm', { name: name }))) {

        return;
    }

    try {
        const response = await fetch(`${API_BASE}/tts-profiles/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        fetchTTSProfiles(); // Reload the list
    } catch (error) {
        alert((i18n.t('settings.ttsProfiles.deleteError') || 'Error deleting') + ': ' + error.message);
    }
}

// Show status message in TTS profile form
function showTTSProfileStatus(message, type) {
    if (!ttsProfileStatusMessage) return;
    ttsProfileStatusMessage.textContent = message;
    ttsProfileStatusMessage.className = `status-message ${type}`;
    if (type === 'success') {
        setTimeout(() => {
            ttsProfileStatusMessage.className = 'status-message';
        }, 3000);
    }
}

// Event listeners for TTS profile management
if (addTtsProfileBtn) addTtsProfileBtn.addEventListener('click', showTTSProfileForm);
if (cancelTtsProfileBtn) cancelTtsProfileBtn.addEventListener('click', hideTTSProfileForm);
if (saveTtsProfileBtn) saveTtsProfileBtn.addEventListener('click', saveTTSProfile);

// ========== Thorsten AI - Raw PCM Streaming ==========
const WS_URL = CONFIG.WS_URL;

async function checkChatServiceAvailable() {
    try {
        const response = await fetch(`${API_BASE}/api/chat-service/status`);
        const data = await response.json();
        return data.data?.enabled !== false;
    } catch (e) {
        // If the status endpoint fails, allow connection attempt (graceful degradation)
        console.warn('Could not check chat service status:', e);
        return true;
    }
}

// Audio Settings
const SAMPLE_RATE = CONFIG.SAMPLE_RATE;
const BUFFER_SIZE = CONFIG.BUFFER_SIZE;

let socket;
let audioContext;
let workletNode;
let input;
let globalStream;
let isRunning = false;
let isThorstenSpeaking = false;

// Audio queue management for interrupt handling
let audioQueue = []; // Now stores pre-decoded AudioBuffers
let currentAudioSource = null;
let isPlaying = false; // Track if audio is currently playing
let playbackContext = null; // Dedicated AudioContext for TTS playback

// PCM streaming state
let pcmPlaybackContext = null;
let pcmNextStartTime = 0;
let pcmStreamActive = false;
let pcmLastSource = null; // Track last scheduled source for onended

// Track the current valid response ID for barge-in
let currentResponseId = null;

// Prevent duplicate service-disabled toasts across all handlers
let _serviceDisabledToastShown = false;
function showServiceDisabledError() {
    if (_serviceDisabledToastShown) return;
    _serviceDisabledToastShown = true;
    const msg = window.i18n.t('chat.serviceDisabled');
    showToast(msg, 'error', 0);
}
function resetServiceDisabledFlag() {
    _serviceDisabledToastShown = false;
}

// UI Elements
const btn = document.getElementById('btn');
const label = document.getElementById('label');
const output = document.getElementById('output');

// Helper function to decode base64 to ArrayBuffer
function base64ToArrayBuffer(base64) {
    var binary_string = window.atob(base64);
    var len = binary_string.length;
    var bytes = new Uint8Array(len);
    for (var i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i);
    }
    return bytes.buffer;
}

// ========== Chat Bubble Management ==========
let currentBubble = null;
let lastBubbleType = null;



function createBubble(type) {
    const bubble = document.createElement('div');
    bubble.classList.add('chat-message');
    if (type === 'bot') {
        bubble.classList.add('bot-message');
    } else {
        bubble.classList.add('user-message');
    }
    output.appendChild(bubble);
    return bubble;
}

function streamToMessage(text, type = 'bot') {

    // Remove initial placeholder text if present
    const initialMsg = window.i18n.t('chat.initialMessage');
    if (output.innerText === initialMsg || output.innerText.includes('START') || output.innerText.includes('Click')) {
        output.innerText = "";
    }

    // Create new bubble if none exists or type switched
    if (!currentBubble || lastBubbleType !== type) {
        currentBubble = createBubble(type);
        lastBubbleType = type;
    }

    // Append text
    currentBubble.innerText += text;

    // Auto-scroll
    output.scrollTop = output.scrollHeight;
}

// Expose for testing/future use
function addMessage(text, type = 'bot') {
    streamToMessage(text, type);
    currentBubble = null; // Force new bubble next time
}


// Send UI Configuration to Backend
function sendUIConfig() {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        console.warn('Cannot send config: WebSocket not open');
        return;
    }

    // Get current theme color from CSS
    const themeColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--accent-color').trim();

    // Include app_id if a micro-app is currently open
    const openApp = getCurrentApp();
    const currentLanguage = window.i18n.getCurrentLanguage();

    const config = {
        type: "config",
        app_id: openApp ? (openApp.id || openApp._id || null) : null,
        ui_state: {
            language: currentLanguage,
            theme_color: themeColor,
        },
        speech_recognition: {
            language: currentLanguage,
        },
        permissions: {
            changeLanguageByAI: true,
            triggerConfetti: true,
            flashScreen: true,
            shakeScreen: true,
            changeThemeColor: true,
            showNotificationBanner: true
        }
    };

    console.log('Sending UI Config to backend:', config);
    socket.send(JSON.stringify(config));
}

// WebSocket Connection
async function connectWebSocket() {
    // Reset dedup flag for new connection attempt
    resetServiceDisabledFlag();

    // Check if chat service is available
    const isAvailable = await checkChatServiceAvailable();
    if (!isAvailable) {
        showServiceDisabledError();
        return;
    }

    // Get authentication token before connecting
    let authToken = null;
    if (window.getAuthToken) {
        authToken = window.getAuthToken();
        if (authToken) {
            console.log('Auth token retrieved for WebSocket authentication');
        }
    }

    socket = new WebSocket(WS_URL);

    // Wait for connection to open before returning
    await new Promise((resolve, reject) => {
        socket.onopen = () => {
            console.log("WebSocket connected");

            // Send authentication event FIRST with auth token
            if (authToken) {
                const authEvent = {
                    type: "auth",
                    token: authToken
                };
                console.log('Sending auth event to server');
                socket.send(JSON.stringify(authEvent));
            } else {
                console.warn('No auth token available - user may not be authenticated');
            }

            // Then send UI configuration to backend
            sendUIConfig();

            resolve();
        };

        socket.onerror = () => reject(new Error('WebSocket connection failed'));
    });

    socket.onmessage = async (e) => {
        // If the message is binary (should not happen with new server code, but good to check)
        if (e.data instanceof Blob) {
            console.warn("Received unexpected binary data");
            return;
        }

        const msg = JSON.parse(e.data);

        if (msg.type === "interrupt") {
            console.log("🛑 Interruption! New Response ID:", msg.response_id);

            // 1. Update the valid response ID
            currentResponseId = msg.response_id;

            // 2. Clear legacy audio queue
            audioQueue = [];
            isPlaying = false;

            // 3. Stop PCM streaming playback
            stopPcmPlayback();

            // 4. Stop currently playing audio (AudioBufferSourceNode)
            if (currentAudioSource) {
                try {
                    currentAudioSource.stop();
                } catch (e) { /* ignore if already stopped */ }
                currentAudioSource = null;
            }

            isThorstenSpeaking = false;
            if (isRunning) label.innerText = window.i18n.t('chat.listening') || 'Listening...';

            // Mark current bubble as finished so next text starts new one
            currentBubble = null;
        }
        else if (msg.type === "audio_start") {
            // New PCM streaming: audio stream begins
            if (msg.response_id === currentResponseId) {
                handleAudioStart(msg);
            }
        }
        else if (msg.type === "audio_chunk") {
            // New PCM streaming: audio fragment
            if (msg.response_id === currentResponseId) {
                handleAudioChunk(msg);
            } else {
                console.log("🗑️ Discarding old audio chunk for ID:", msg.response_id);
            }
        }
        else if (msg.type === "audio_end") {
            // New PCM streaming: stream finished
            if (msg.response_id === currentResponseId) {
                handleAudioEnd(msg);
            }
        }
        else if (msg.type === "audio") {
            // Legacy: single encoded audio chunk (MP3/OGG fallback)
            if (msg.response_id === currentResponseId) {
                const audioData = base64ToArrayBuffer(msg.data);
                queueAudio(audioData);
            } else {
                console.log("🗑️ Discarding old audio packet for ID:", msg.response_id);
            }
        }
        else if (msg.type === "user" || msg.type === "transcription") {
            // Handler for user transcription messages (STT results)
            console.log("👤 User transcription received:", msg.content);

            if (msg.content) {
                currentBubble = null; // Force new bubble for user message
                streamToMessage(msg.content + " ", 'user');
            }
        }
        else if (msg.type === "assistant_message") {
            // NEW: Handler for assistant message bubbles (sent after 500ms visual flush timeout)
            console.log("🤖 Assistant message bubble received:", msg.content);

            if (msg.content) {
                currentBubble = null; // Force new bubble for each assistant message
                streamToMessage(msg.content, 'bot');
            }
        }
        else if (msg.type === "text") {
            // ========== RAW BACKEND TEXT LOGGING ==========
            // Log EXACTLY what comes from backend - NO MODIFICATIONS!
            console.log("📥 RAW BACKEND TEXT - START");
            console.log("Full message object:", JSON.stringify(msg, null, 2));
            console.log("Content type:", typeof msg.content);
            console.log("Content length:", msg.content ? msg.content.length : 0);
            console.log("Raw content (exactly as received):");
            console.log(msg.content);
            console.log("Character-by-character breakdown:");
            if (msg.content) {
                for (let i = 0; i < msg.content.length; i++) {
                    const char = msg.content[i];
                    const code = char.charCodeAt(0);
                    console.log(`  [${i}]: "${char}" (code: ${code}${code === 32 ? ' <-- SPACE' : ''}${code === 10 ? ' <-- NEWLINE' : ''})`);
                }
            }
            console.log("📥 RAW BACKEND TEXT - END");
            // ========== END LOGGING ==========

            // Parse and separate user/bot messages
            let content = msg.content;

            // Check if the message contains conversation markers
            if (content.includes("Du:") || content.includes("Thorsten:")) {
                // Split by common patterns
                const lines = content.split(/\n+/);

                for (let line of lines) {
                    if (!line) continue;

                    if (line.startsWith("Du:")) {
                        // User message
                        const text = line.substring(3).trim();
                        if (text) {
                            currentBubble = null; // Force new bubble
                            streamToMessage(text, 'user');
                        }
                    } else if (line.startsWith("Thorsten:")) {
                        // Bot message
                        const text = line.substring(9).trim();
                        if (text) {
                            currentBubble = null; // Force new bubble
                            streamToMessage(text, 'bot');
                        }
                    } else {
                        // Continue with current speaker (bot by default)
                        streamToMessage(line, 'bot');
                    }
                }
            } else {
                // No markers, treat as bot message
                streamToMessage(content, 'bot');
            }
        }
        else if (msg.type === "function_call") {
            console.log("🤖 AI Function Call:", msg.name, msg.arguments);
            const success = handleAIFunctionCall(msg.name, msg.arguments || {});
            if (!success) {
                // Not a built-in UI function - try forwarding to micro-app iframe
                if (isAppOpen()) {
                    const callId = msg.call_id || msg.id || `fc_${Date.now()}`;
                    console.log("🔧 Forwarding to micro-app:", msg.name, callId);
                    sendToolCallToApp(callId, msg.name, msg.arguments || {})
                        .then(result => {
                            socket.send(JSON.stringify({
                                type: 'tool_result',
                                call_id: callId,
                                result: result
                            }));
                            console.log("✅ Tool result sent to backend:", callId);
                        })
                        .catch(error => {
                            socket.send(JSON.stringify({
                                type: 'tool_result',
                                call_id: callId,
                                error: error.message
                            }));
                            console.error("❌ Tool call failed:", callId, error.message);
                        });
                } else {
                    console.warn(`Unknown AI function (no app open): ${msg.name}`);
                }
            }
        }
        else if (msg.type === "tool_call") {
            // AI Tool Call for micro-app: forward to iframe and return result to backend
            console.log("🔧 AI Tool Call for micro-app:", msg.call_id, msg.tool, msg.arguments);

            if (!isAppOpen()) {
                // No app open - send error back to backend
                socket.send(JSON.stringify({
                    type: 'tool_result',
                    call_id: msg.call_id,
                    error: 'No micro-app is currently open'
                }));
                return;
            }

            // Forward to iframe and wait for result
            sendToolCallToApp(msg.call_id, msg.tool, msg.arguments || {})
                .then(result => {
                    // Send result back to backend
                    socket.send(JSON.stringify({
                        type: 'tool_result',
                        call_id: msg.call_id,
                        result: result
                    }));
                    console.log("✅ Tool result sent to backend:", msg.call_id);
                })
                .catch(error => {
                    // Send error back to backend
                    socket.send(JSON.stringify({
                        type: 'tool_result',
                        call_id: msg.call_id,
                        error: error.message
                    }));
                    console.error("❌ Tool call failed:", msg.call_id, error.message);
                });
        }
        else if (msg.type === "ui_action") {
            // NEW: Generic UI Action handler - Backend can call any UI function dynamically
            console.log("🎯 UI Action:", msg.action, msg.payload);
            const action = msg.action; // z.B. "changeLanguageByAI"
            const payload = msg.payload || {};

            // Try to find and execute the function
            if (typeof window[action] === "function") {
                try {
                    // Call function with payload values as arguments
                    const result = window[action](...Object.values(payload));
                    console.log(`✓ UI Action '${action}' executed successfully`, result);
                } catch (error) {
                    console.error(`✗ UI Action '${action}' failed:`, error);
                }
            } else {
                console.error("Unknown UI function:", action);
            }
        }
        else if (msg.type === "error") {
            // NEW: Error handler - display backend errors to user
            console.error("Backend error:", msg.message);

            // Check for known error types and show localized messages
            const errorMsg = msg.message.toLowerCase();
            if (errorMsg.includes('credit') && (errorMsg.includes('limit') || errorMsg.includes('insufficient') || errorMsg.includes('exhausted') || errorMsg.includes('quota'))) {
                // Credit limit error - show localized message
                const creditLimitError = window.i18n.t('chat.creditLimitError');
                showToast(creditLimitError, 'error', 0);
            } else if (errorMsg.includes('chat') && (errorMsg.includes('disabled') || errorMsg.includes('unavailable'))) {
                // Chat service disabled - show localized message (deduplicated)
                showServiceDisabledError();
            } else {
                // Other errors - parse and display
                const humanReadableError = parseErrorMessage(msg.message);
                showToast(humanReadableError, 'error', 0);
            }
        }
    };

    socket.onclose = (event) => {
        console.log("WebSocket disconnected");
        stopAudioRecording();
        if (event.code === 4003) {
            showServiceDisabledError();
        }
    };

    socket.onerror = (err) => {
        console.error("WebSocket error:", err);
        stopAudioRecording();
    };
}

// Start Audio Recording with Raw PCM
// Start Audio Recording with Raw PCM
async function startAudioRecording() {
    try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: SAMPLE_RATE
        });

        // Load AudioWorklet module
        try {
            await audioContext.audioWorklet.addModule('/audio-processor.js');
        } catch (err) {
            console.error("Error loading AudioWorklet:", err);
            throw new Error("AudioWorklet could not be loaded.");
        }

        globalStream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        });

        input = audioContext.createMediaStreamSource(globalStream);

        // AudioWorklet for raw audio access (replaces ScriptProcessor)
        workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');

        workletNode.port.onmessage = (e) => {
            // Receive Int16 ArrayBuffer from worklet and send to socket
            if (socket && socket.readyState === WebSocket.OPEN && !isThorstenSpeaking && isRunning) {
                socket.send(e.data);
            }
        };

        input.connect(workletNode);
        workletNode.connect(audioContext.destination); // Required for processor to run

        label.innerText = window.i18n.t('chat.listening') || 'Listening...';
        btn.innerText = window.i18n.t('chat.stop') || 'STOP';
        btn.classList.add('active');

    } catch (e) {
        console.error("Audio start error:", e);
        alert("Microphone access denied or error: " + e.message);
        stop();
    }
}

// Stop Audio Recording
function stopAudioRecording() {
    if (globalStream) {
        globalStream.getTracks().forEach(track => track.stop());
        globalStream = null;
    }
    if (workletNode) {
        workletNode.disconnect();
        workletNode.port.onmessage = null;
        workletNode = null;
    }
    if (input) {
        input.disconnect();
        input = null;
    }

    if (audioContext) {
        // Only close if not already closed to avoid InvalidStateError
        if (audioContext.state !== 'closed') {
            audioContext.close().catch(err => console.warn('Error closing AudioContext:', err));
        }
        audioContext = null;
    }

    // Close playback context (legacy)
    if (playbackContext) {
        if (playbackContext.state !== 'closed') {
            playbackContext.close().catch(err => console.warn('Error closing playback context:', err));
        }
        playbackContext = null;
    }

    // Close PCM streaming context
    stopPcmPlayback();

    isRunning = false;
    label.innerText = window.i18n.t('chat.ready') || 'Ready';
    btn.innerText = window.i18n.t('chat.start') || 'START';
    btn.classList.remove('active');
}

// ========== PCM Streaming Playback (audio_start/audio_chunk/audio_end) ==========

function handleAudioStart(msg) {
    // Stop any previous PCM stream
    stopPcmPlayback();

    const sampleRate = msg.sample_rate || 24000;
    pcmPlaybackContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate });
    pcmNextStartTime = 0;
    pcmStreamActive = true;
    pcmLastSource = null;
    isThorstenSpeaking = true;
    label.innerText = window.i18n.t('chat.speaking') || 'Speaking...';
}

function handleAudioChunk(msg) {
    if (!pcmPlaybackContext || !pcmStreamActive) return;

    // base64 → Int16 → Float32
    const raw = window.atob(msg.data);
    const bytes = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);

    const int16 = new Int16Array(bytes.buffer);
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 32768;
    }

    const ctx = pcmPlaybackContext;
    if (ctx.state === 'suspended') ctx.resume();

    const buffer = ctx.createBuffer(1, float32.length, ctx.sampleRate);
    buffer.getChannelData(0).set(float32);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    currentAudioSource = source; // Track for interrupt stopping

    const now = ctx.currentTime;
    if (pcmNextStartTime < now) pcmNextStartTime = now;
    source.start(pcmNextStartTime);
    pcmNextStartTime += buffer.duration;

    // Track last source so we know when playback truly ends
    pcmLastSource = source;
}

function handleAudioEnd(msg) {
    pcmStreamActive = false;

    // When last scheduled chunk finishes playing, reset speaking state
    if (pcmLastSource) {
        pcmLastSource.onended = () => {
            isThorstenSpeaking = false;
            currentAudioSource = null;
            pcmLastSource = null;
            if (isRunning) label.innerText = window.i18n.t('chat.listening') || 'Listening...';
        };
    } else {
        isThorstenSpeaking = false;
        if (isRunning) label.innerText = window.i18n.t('chat.listening') || 'Listening...';
    }
}

function stopPcmPlayback() {
    pcmStreamActive = false;
    pcmNextStartTime = 0;
    pcmLastSource = null;
    if (pcmPlaybackContext && pcmPlaybackContext.state !== 'closed') {
        pcmPlaybackContext.close().catch(() => {});
    }
    pcmPlaybackContext = null;
}

// ========== Legacy encoded audio playback (MP3/OGG fallback) ==========

// Get or create the playback AudioContext (reused across chunks)
function getPlaybackContext() {
    if (!playbackContext || playbackContext.state === 'closed') {
        playbackContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (playbackContext.state === 'suspended') {
        playbackContext.resume();
    }
    return playbackContext;
}

// Call this when you receive new audio data — pre-decode immediately
function queueAudio(audioData) {
    const ctx = getPlaybackContext();
    // Pre-decode the audio in the background while previous chunks play
    ctx.decodeAudioData(audioData.slice(0)) // slice to avoid detached buffer issues
        .then(audioBuffer => {
            audioQueue.push(audioBuffer);
            processQueue();
        })
        .catch(err => {
            console.error("Audio decode error:", err);
        });
}

async function processQueue() {
    if (isPlaying || audioQueue.length === 0) return;

    isPlaying = true;
    const audioBuffer = audioQueue.shift();

    try {
        await playAudioBuffer(audioBuffer);
    } catch (e) {
        console.error("Playback error:", e);
    } finally {
        isPlaying = false;
        processQueue(); // Process next item
    }
}

// Play pre-decoded AudioBuffer via Web Audio API (much faster than HTML5 Audio)
function playAudioBuffer(audioBuffer) {
    return new Promise((resolve, reject) => {
        isThorstenSpeaking = true;
        label.innerText = window.i18n.t('chat.speaking') || 'Speaking...';

        const ctx = getPlaybackContext();
        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(ctx.destination);

        currentAudioSource = source; // Track for stopping

        source.onended = () => {
            isThorstenSpeaking = false;
            currentAudioSource = null;
            if (audioQueue.length === 0 && isRunning) label.innerText = window.i18n.t('chat.listening') || 'Listening...';
            resolve();
        };

        source.start(0);
    });
}



// Stop Everything
function stop() {
    if (socket) socket.close();
    stopAudioRecording();
}

// Button Click Handler
btn.onclick = async () => {
    if (!isRunning) {
        // Start Voice Mode
        isRunning = true;

        try {
            if (!socket || socket.readyState !== WebSocket.OPEN) {
                await connectWebSocket();
            }
            // Connection is now open, start audio
            startAudioRecording();
        } catch (e) {
            console.error('Failed to start voice mode:', e);
            isRunning = false;
        }
    } else {
        // Stop Voice Mode
        stop();
    }
};

// ========== Text Chat Functionality ==========
const chatInput = document.getElementById('chatInput');
const sendMessageBtn = document.getElementById('sendMessageBtn');

function sendTextMessage() {
    const text = chatInput.value.trim();
    if (!text) return;

    if (!socket || socket.readyState !== WebSocket.OPEN) {
        // If not connected, try to connect first
        // Note: We do NOT set isRunning = true here, so it's "Text Only Mode"
        connectWebSocket();

        // Wait for connection (simple polling for this example)
        const checkConnection = setInterval(() => {
            if (socket && socket.readyState === WebSocket.OPEN) {
                clearInterval(checkConnection);
                send();
            }
        }, 100);

        // Timeout after 5 seconds
        setTimeout(() => clearInterval(checkConnection), 5000);

        function send() {
            socket.send(JSON.stringify({
                type: 'text',
                content: text
            }));

            // Show user message immediately
            currentBubble = null; // Force new bubble
            streamToMessage(text, 'user');

            chatInput.value = '';
            // Reset height
            chatInput.style.height = 'auto';
        }
    } else {
        // Already connected
        socket.send(JSON.stringify({
            type: 'text',
            content: text
        }));

        // Show user message immediately
        currentBubble = null; // Force new bubble
        streamToMessage(text, 'user');

        chatInput.value = '';
        // Reset height
        chatInput.style.height = 'auto';
    }
}

// Auto-resize textarea
chatInput.addEventListener('input', function () {
    this.style.height = 'auto';
    this.style.height = (this.scrollHeight) + 'px';
});

// Send on Enter (but allow Shift+Enter for new line)
chatInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendTextMessage();
    }
});

sendMessageBtn.addEventListener('click', sendTextMessage);

// ========== Initialize Text Color Contrast on Page Load ==========
function initializeTextColorContrast() {
    // Get current accent color from CSS
    const currentColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--accent-color').trim();

    if (currentColor && (currentColor.startsWith('#') || currentColor.startsWith('rgb'))) {
        // Apply contrast adjustment for current color
        const textColor = getContrastingTextColor(currentColor);
        document.documentElement.style.setProperty('--user-message-text-color', textColor);

        const labelColor = isLightColor(currentColor) ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 245, 230, 0.9)';
        document.documentElement.style.setProperty('--user-message-label-color', labelColor);

        console.log(`Initialized text contrast for color ${currentColor}: text=${textColor}, label=${labelColor}`);
    }
}

// Run initialization when page loads
initializeTextColorContrast();

// ========== Chat Modal Control ==========
const ChatModal = {
    init() {
        this.modal = document.getElementById('chatModal');
        this.icon = document.getElementById('chatIcon');
        this.minimizeBtn = document.getElementById('minimizeChatBtn');
        this.closeBtn = document.getElementById('closeChatBtn');
        this.output = document.getElementById('output');
        this.chatInput = document.getElementById('chatInput');
        this.sendBtn = document.getElementById('sendMessageBtn');
        this.startBtn = document.getElementById('btn');

        // Hide chat icon initially - will be shown by auth module if user is logged in
        if (this.icon) {
            this.icon.style.display = 'none';
        }
        if (this.modal) {
            this.modal.classList.add('hidden');
        }

        // Check if user is signed in before setting up chat
        if (!window.isUserSignedIn || !window.isUserSignedIn()) {
            // User not signed in - keep chat hidden
            console.log('Chat disabled - user not signed in');
            return;
        }

        // User is signed in - show chat icon and setup event listeners
        if (this.icon) {
            this.icon.style.display = 'flex';
        }

        // Load saved state
        const savedState = localStorage.getItem('chatModalState') || 'minimized';

        // Setup event listeners
        if (this.icon) this.icon.addEventListener('click', () => this.open());
        if (this.minimizeBtn) this.minimizeBtn.addEventListener('click', () => this.minimize());
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

        // Initialize state
        if (savedState === 'open') {
            this.open();
        } else {
            this.minimize();
        }
    },

    open() {
        // Check if user is signed in
        if (!window.isUserSignedIn || !window.isUserSignedIn()) {
            console.log('Chat blocked - user not signed in');
            return;
        }

        if (this.modal) this.modal.classList.remove('hidden', 'minimized');
        if (this.icon) this.icon.classList.add('hidden');
        localStorage.setItem('chatModalState', 'open');

        // User is signed in - enable chat functionality
        this.enableChat();
    },

    showLoginPrompt() {
        // Disable chat controls
        this.chatInput.disabled = true;
        this.sendBtn.disabled = true;
        this.startBtn.disabled = true;

        // Clear output and show login message
        this.output.innerHTML = '';

        // Create login prompt container
        const loginContainer = document.createElement('div');
        loginContainer.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            padding: 20px;
            text-align: center;
            gap: 20px;
        `;

        // Create message element
        const message = document.createElement('p');
        message.style.cssText = `
            font-size: 1.1em;
            color: var(--text-color);
            margin: 0;
            line-height: 1.5;
        `;
        message.textContent = window.i18n?.t('chat.magicLinkRequired') || 'Please use a magic link to log in.';

        // Append elements (no login button - magic link only)
        loginContainer.appendChild(message);
        this.output.appendChild(loginContainer);

        console.log('User not logged in - showing magic link prompt');
    },

    enableChat() {
        // Enable chat controls
        if (this.chatInput) this.chatInput.disabled = false;
        if (this.sendBtn) this.sendBtn.disabled = false;
        if (this.startBtn) this.startBtn.disabled = false;

        // Show session tracking indicator
        const sessionIndicator = document.getElementById('sessionTrackingIndicator');
        if (sessionIndicator) {
            sessionIndicator.style.display = 'block';
        }

        console.log('User logged in - chat enabled');
    },

    minimize() {
        if (this.modal) this.modal.classList.add('minimized');
        if (this.icon) this.icon.classList.remove('hidden');
        localStorage.setItem('chatModalState', 'minimized');
    },

    close() {
        if (this.modal) this.modal.classList.add('hidden');
        if (this.icon) this.icon.classList.remove('hidden');
        localStorage.setItem('chatModalState', 'closed');
    }
};

// Initialize Chat Modal on page load
window.addEventListener('DOMContentLoaded', () => {
    ChatModal.init();

    // Initialize Cookie Consent Banner
    initCookieBanner();
});

// ========== Cookie Consent Banner ==========
function initCookieBanner() {
    const cookieBanner = document.getElementById('cookieBanner');
    const acceptBtn = document.getElementById('acceptCookies');

    if (!cookieBanner || !acceptBtn) {
        console.warn('Cookie banner elements not found');
        return;
    }

    // Check if user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookieConsent');

    if (!cookieConsent) {
        // Show banner with slide-up animation after a short delay
        setTimeout(() => {
            cookieBanner.classList.remove('hidden');
            cookieBanner.classList.add('show');
        }, 500);
    }

    // Handle accept button click
    acceptBtn.addEventListener('click', () => {
        // Save consent to localStorage
        localStorage.setItem('cookieConsent', 'accepted');
        localStorage.setItem('cookieConsentDate', new Date().toISOString());

        // Hide banner with slide-down animation
        cookieBanner.classList.remove('show');
        cookieBanner.classList.add('hidden');

        console.log('Cookie consent accepted');
    });
}

// Make cookie banner functions globally available
window.initCookieBanner = initCookieBanner;
